import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useNow, useRuntime } from './context';
import { formatClock, formatDayLong, formatMoney, isoDayIn } from '../mirror/time';
import { deviceT, useLocale, useT } from '../messages';
import type { T } from '../messages';
import { BookSheet } from './BookSheet';
import type { BookSheetPrefill } from './BookSheet';
import { AppointmentSheet, birthdaysWithin } from './AppointmentSheet';
import { AddClientNote } from './AddClientNote';
import { AppointmentRequests, Inquiries } from './Requests';
import { CheckoutSheet } from './Checkout';
import type { CheckoutPrefill } from './Checkout';
import { hasStagedReservation, takeStagedReservation } from './till';
import { PaymentLinksPanel } from './PaymentLinks';
import { ActionGlyph } from './brand';
import { useActionIcon } from './useRemote';
import { WriteRefusal, useSeat, useSeatRefusal, useWriteAttempt } from './seat';
import { TENDER_ORDER } from './Reports';
import type { MirroredAppointment } from '../db/schema';
import type { Appointment } from '../wire';

/**
 * Today — the screen the iPad sits on all day.
 *
 * Two things about it are §8 rather than layout.
 *
 * **The list is the mirror plus the outbox.** A booking the owner just made
 * appears immediately, marked *pending*, whether or not there is a network,
 * because it is read out of the queued intent rather than out of the mirror. The
 * moment it lands the intent is `done`, the real row is in the mirror, and the
 * same line redraws with a server id. There is no reconciliation pass to get
 * wrong: the pending row stops existing exactly when the real one starts.
 *
 * **A pending create is hidden once a matching real row exists.** During the few
 * hundred milliseconds between the server committing and this device settling
 * the intent, an SSE-driven refetch can put the real appointment in the mirror
 * first. Showing both would be a duplicate; picking one on the *display* side is
 * not a merge of records — nothing is written, nothing is combined, and if the
 * two ever genuinely disagreed the mirror's copy is the one shown, because the
 * cloud is authoritative.
 *
 * **Every write control is drawn for `can_write` only** (L15, `seat.tsx`): the
 * walk-in door, the till, the row's close-out, done, no-show and cancel. A
 * `readonly` seat and the console's support look read the same day and have
 * nothing to press — hidden, not disabled, the rule the other screens follow.
 * The sheet a row opens is still a read, so the tap stays.
 */
export function Today(): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const action = useActionIcon();
  const t = useT();
  const locale = useLocale();
  const [booking, setBooking] = useState<BookSheetPrefill | null>(null);
  const [checkout, setCheckout] = useState<CheckoutPrefill | null>(null);
  /**
   * Which visit's pay links are open.
   *
   * The deposit link hangs off the appointment because that is where the
   * conversation happens — "hold it for me, I'll pay the deposit now" is said
   * about a booking, not in a payments screen. Today's row **is** this app's
   * appointment detail (there is no other), so this is where §3.7's staff
   * affordance goes. Unlike everything else on this row it is an *online*
   * action: a pay link is a URL a server mints against a live PayPal
   * configuration, and there is no honest optimistic form of one.
   */
  const [links, setLinks] = useState<{ id: string; name: string } | null>(null);
  /**
   * Which visit is open (B1).
   *
   * Held as the row rather than as an id, because a pending booking has no
   * server id yet and the sheet is worth having for one: the note the owner
   * wants to write about the person who just walked in is the note this whole
   * feature exists for.
   */
  const [detail, setDetail] = useState<Appointment | null>(null);

  const { session, canWrite } = useSeat();
  const timezone = session?.tenant.timezone ?? 'UTC';
  const today = isoDayIn(timezone, runtime.now());
  // The till is a write too: a settlement is a ledger row.
  const canSell = canWrite && (session?.modules ?? []).includes('sales');
  /**
   * Where a refused tap says so. `attempt` catches the outbox's typed refusal
   * on the row verbs, which are fire-and-forget; `openBooking` is for the one
   * door this screen does not draw itself — the inquiry panel's *Book this*
   * (`Requests.tsx`) — which still hands a prefill up here and is answered with
   * the sentence rather than a sheet that would refuse on submit.
   */
  const { refusal, attempt, clear } = useWriteAttempt();
  const [doorRefusal, setDoorRefusal] = useState<string | null>(null);
  const seatRefusal = useSeatRefusal();
  const openBooking = (prefill: BookSheetPrefill): void => {
    if (!canWrite) {
      setDoorRefusal(seatRefusal);
      return;
    }
    setDoorRefusal(null);
    setBooking(prefill);
  };

  const mirrored = useLive(() => runtime.mirror.day(today), [], [today]);
  const pending = useLive(() => runtime.outbox.pending(), []);

  /**
   * Which visits have a checkout still in the queue.
   *
   * Read off the outbox rather than off the mirror, for the same reason the
   * optimistic booking row is: offline, the settlement has not happened yet and
   * the appointment is still `confirmed`. Saying "checkout waiting to send" is
   * the honest sentence — it is neither "done" (the ledger has not seen it) nor
   * nothing at all (the owner did the work and must not be asked to do it
   * twice).
   */
  const checkoutQueued = new Set(
    pending
      .filter((entry) => entry.kind === 'settlement.create')
      .map((entry) => entry.target['appointment_id'])
      .filter((id): id is string => id !== undefined),
  );

  const optimistic = pending
    .map((entry) => entry.optimistic)
    .filter((row): row is Appointment => row !== undefined)
    .filter((row) => isoDayIn(timezone, row.slot.start) === today)
    .filter((row) => !mirrored.some((real) => sameBooking(real, row)));

  const rows: DisplayRow[] = [
    ...mirrored.map((a) => ({ appointment: a, pending: false })),
    ...optimistic.map((a) => ({ appointment: a, pending: true })),
  ].sort((a, b) => a.appointment.slot.start.localeCompare(b.appointment.slot.start));

  return (
    <section className="view">
      <div className="view__header">
        <h1>{formatDayLong(today, timezone, locale)}</h1>
        <div className="row-actions">
          {canSell ? (
            /*
             * **The till, and the hand-over from the shelf** (C1 → B4).
             *
             * One button, because it opens one sheet. `ui/till.ts` holds at most
             * one staged reservation id; taking it *here*, in the click handler,
             * rather than inside `CheckoutSheet`'s mount is deliberate: this app
             * runs under `StrictMode`, which double-invokes render bodies and
             * remounts effects, and a take that ran twice would hand the first
             * render the reservation and the second one `null`. An event handler
             * runs exactly once. `till.ts` says so at its own definition.
             */
            <button
              type="button"
              className="button"
              onClick={() => {
                const staged = takeStagedReservation();
                setCheckout(staged === null ? {} : { reservationId: staged });
              }}
            >
              {hasStagedReservation() ? t('today.ringUpPickup') : t('today.quickSale')}
            </button>
          ) : null}
          {canWrite ? (
            <button
              type="button"
              className={`button button--primary${action.icon === 'heart' ? ' button--heart' : ''}`}
              onClick={() => openBooking({ source: 'walk_in' })}
            >
              <ActionGlyph icon={action.icon} />
              {t('today.walkIn')}
            </button>
          ) : null}
        </div>
      </div>

      <WriteRefusal message={doorRefusal ?? refusal} />

      <Takings timezone={timezone} waiting={pending.filter(isQueuedSale).length} />

      <Birthdays timezone={timezone} />

      {/* The empty state names the affordance a heart tenant can actually see
          (`docs/lovespa-personality.md`: "copy follows the icon choice"). On the
          default it is the sentence that was always here — the named set adds a
          clause, it does not re-word the app for tenants who chose nothing. */}
      {rows.length === 0 ? (
        <p className="empty">
          {t('today.empty')}
          {action.icon === 'heart' ? t('today.emptyHeart', { button: action.buttonName }) : null}
        </p>
      ) : (
        <ul className="day-list">
          {rows.map((row) => (
            <li
              key={row.appointment.appointment_id}
              className={`day-row day-row--${row.appointment.status}${row.pending ? ' day-row--pending' : ''}`}
            >
              <div className="day-row__time">
                <strong>{formatClock(row.appointment.slot.start, timezone, locale)}</strong>
                <small>{t('today.duration', { minutes: row.appointment.duration_min })}</small>
              </div>

              {/*
               * **The tap** (B1). The whole "who" block is the button, because
               * that is what a finger aims at — and it is a `button` rather than
               * a click handler on a `div` so the keyboard and the screen reader
               * get it too.
               *
               * It opens the sheet for a *pending* row as well. A booking that
               * has not reached the server yet still has a guest, and the note
               * the owner wants to write about her is exactly the note that gets
               * lost otherwise.
               */}
              <button
                type="button"
                className="day-row__who day-row__who--tappable"
                onClick={() => setDetail(row.appointment)}
              >
                <strong>{row.appointment.client.display_name}</strong>
                <small>
                  {row.appointment.service_name}
                  {row.appointment.resource_name === null
                    ? ''
                    : ` · ${row.appointment.resource_name}`}
                </small>
                {row.appointment.notes === null ? null : (
                  <small className="muted">{row.appointment.notes}</small>
                )}
              </button>

              <div className="day-row__tail">
                {row.pending ? (
                  <span className="badge badge--pending">{t('today.waitingToSend')}</span>
                ) : checkoutQueued.has(row.appointment.appointment_id) ? (
                  <span className="badge badge--pending">{t('today.checkoutWaiting')}</span>
                ) : (
                  <span className={`badge badge--source-${row.appointment.source}`}>
                    {sourceWord(row.appointment.source, t)}
                  </span>
                )}
                {canWrite &&
                !row.pending &&
                row.appointment.status === 'confirmed' &&
                !checkoutQueued.has(row.appointment.appointment_id) ? (
                  <div className="row-actions">
                    {canSell ? (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() =>
                          setCheckout({ appointment: row.appointment as MirroredAppointment })
                        }
                      >
                        {t('today.closeOut')}
                      </button>
                    ) : null}
                    {canSell ? (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() =>
                          setLinks({
                            id: row.appointment.appointment_id,
                            name: row.appointment.service_name,
                          })
                        }
                      >
                        {t('today.paymentLink')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        attempt(
                          actions.setStatus({
                            appointmentId: row.appointment.appointment_id,
                            status: 'completed',
                            clientName: row.appointment.client.display_name,
                            whenIso: row.appointment.slot.start,
                          }),
                        )
                      }
                    >
                      {t('today.done')}
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        attempt(
                          actions.setStatus({
                            appointmentId: row.appointment.appointment_id,
                            status: 'no_show',
                            clientName: row.appointment.client.display_name,
                            whenIso: row.appointment.slot.start,
                          }),
                        )
                      }
                    >
                      {t('today.noShow')}
                    </button>
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      onClick={() =>
                        attempt(
                          actions.cancel({
                            appointmentId: row.appointment.appointment_id,
                            clientName: row.appointment.client.display_name,
                            whenIso: row.appointment.slot.start,
                          }),
                        )
                      }
                    >
                      {t('today.cancel')}
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/*
       * **Two panels, two different things, and the headings say which**
       * (`docs/lovespa-discovery.md` §2.3). Both arrived under the word
       * "Requests" and one of them had to give it up, because an owner reading
       * two identical headings has to open both to find out which is which.
       *
       * **Appointment requests** (A5) is first: a real time, a verified number,
       * a held slot, and three answers — confirm, decline, propose another. It
       * is the one with a decision in it.
       *
       * **Messages** is the anonymous inquiry list — somebody typed words into
       * a form. No slot, no number anybody proved, and one verb: "answered",
       * which since X1 is the server's fact rather than a tick on one iPad
       * (`Inquiries` in `Requests.tsx` says how). It sits underneath.
       *
       * Both sit under the day because that is what they are: work that
       * arrived, not work that is scheduled.
       */}
      <AppointmentRequests />

      <Inquiries onBook={openBooking} />

      {detail === null ? null : (
        <AppointmentSheet appointment={detail} onClose={() => setDetail(null)} />
      )}

      {booking !== null ? (
        <BookSheet
          prefill={booking}
          onDone={() => {
            clear();
            setBooking(null);
          }}
          onCancel={() => setBooking(null)}
        />
      ) : null}

      {checkout !== null ? (
        <CheckoutSheet
          prefill={checkout}
          onDone={() => setCheckout(null)}
          onCancel={() => setCheckout(null)}
          /*
           * **B1's "one-tap add a note after checkout", filled in.** The register
           * (B4) left a hole here rather than importing a component from files it
           * did not own; this is the host putting the desk agent's component into
           * it. Nothing in `Checkout.tsx` knows what it draws.
           *
           * **Nothing at all for a walk-in.** The shop's own account is a real
           * `clients` row and a real id, but it is nobody in particular — a note
           * filed against it is a note the owner will never find again, and a
           * button that files one is worse than no button.
           */
          afterCheckout={({ clientId, clientName }) =>
            clientId === null ? null : (
              <AddClientNote clientId={clientId} {...(clientName === null ? {} : { clientName })} />
            )
          }
        />
      ) : null}

      {links !== null ? (
        <PaymentLinksPanel
          appointmentId={links.id}
          itemName={links.name}
          onClose={() => setLinks(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * **Birthdays, today and tomorrow** (B2; `docs/lovespa-discovery.md` §3 — the
 * owner's own word for it was *"that's helpful"*).
 *
 * One quiet line, and **nothing at all when there are none** — which is most
 * days, and a heading over an empty list every morning is chrome the Today
 * screen does not need. Same rule as the Requests panel below.
 *
 * Read from the mirrored client book, so it is there in a tunnel. `birthday` is
 * a full `YYYY-MM-DD`, and the comparison is month-and-day **in the tenant's
 * timezone**: the year is when she was born, and an iPad that travels must not
 * move whose birthday it is. 29 February reads as the 28th in a year that has
 * no 29th (`daysUntilBirthday`).
 *
 * Deliberately today and tomorrow only. The appointment sheet's own line looks a
 * week ahead, because there the question is "is this the last time I see her
 * before it"; here the question is "is there a card to write this morning".
 *
 * **Names, not a link.** Tapping through to a client from a greeting is a
 * navigation this screen does not own, and the useful act — saying happy
 * birthday — happens in a text message she sends herself.
 */
function Birthdays({ timezone }: { timezone: string }): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const clients = useLive(() => runtime.mirror.clients(), []);

  const soon = birthdaysWithin(clients, timezone, runtime.now(), 1);
  if (soon.length === 0) return null;

  const todays = soon.filter((row) => row.days === 0).map((row) => row.client.display_name);
  const tomorrows = soon.filter((row) => row.days === 1).map((row) => row.client.display_name);

  const parts: string[] = [];
  if (todays.length > 0) parts.push(t('today.birthdaysToday', { names: todays.join(', ') }));
  if (tomorrows.length > 0) {
    parts.push(t('today.birthdaysTomorrow', { names: tomorrows.join(', ') }));
  }

  return <p className="birthdays">{t('today.birthdays', { list: parts.join('; ') })}</p>;
}

/**
 * **At the counter today** — what has been taken since the tenant's midnight,
 * by tender, net of what went back.
 *
 * The number is the ledger's, never this device's: `mirror/today-takings.ts`
 * says why, and it is `Reports`' argument one day wide. So the line reads the
 * last answer from IndexedDB, asks again from an effect, and says how old the
 * answer is when it is old enough to matter. Tips are in it because they are
 * in the drawer; gift credit and a certificate *redeemed* at the till are not,
 * because nothing was handed over. Only the six external tenders are listed,
 * and the total is their sum, so the parts on the line always add up to the
 * number in front.
 *
 * **Gift cards sold today are in it** (owner's decision 2 of 2026-09-23,
 * migration 0070). A $170 card paid by Venmo is $170 that arrived on Venmo, and
 * the owner counting her apps at night counts it there. The server keeps the
 * two apart — `certificate_by_tender` beside `by_tender`, so the counter's own
 * equation (`Σ by_tender − tip = by_source.settlement_cents`) stays true — and
 * this line adds them per tender, because "what came in on Venmo today" is the
 * question it answers. A card sold before the question existed has no tender
 * and is on no line here, which is honest: the device does not know how it was
 * paid, and guessing `cash` would be a drawer that does not balance.
 *
 * **Who sees it is who sees Reports** — the `reporting` module, nothing about
 * the seat. The Reports tab is drawn for a `readonly` seat and the support look
 * alike, and a line that hid the day's money from somebody who can open the
 * month's one tab over would be a wall with a door beside it.
 *
 * **When it asks again.** On mount; on `settlement.recorded`,
 * `refund.recorded` or `certificate.issued` over the event stream, which covers
 * this iPad's own sale or gift card the moment the server commits it, one rung
 * up on the phone, and a refund given from a client's page; and when the count
 * of queued sales goes *down*, which is the outbox draining after a tunnel —
 * exactly the moment the stream is most likely still reconnecting. No poll:
 * every way the number can change is one of those three.
 *
 * **Queued sales are a count** — checkouts and gift cards alike. A
 * `settlement.create` in the outbox carries no total (B7; `outbox/intents.ts`),
 * and a `certificate.issue` for a service carries no value either (the server
 * snapshots the catalog's price), so the device cannot say what they came to and
 * does not try. "+ 2 sales waiting to send" is the true sentence; adding a
 * preview to the ledger's number would print a total nobody decided.
 */
function Takings({ timezone, waiting }: { timezone: string; waiting: number }): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const { session } = useSeat();
  // The tick ages the staleness clause and rolls the day over at midnight
  // without waiting for the list above to redraw; the number is the runtime's.
  useNow();
  const now = runtime.now();
  const today = isoDayIn(timezone, now);
  const entitled = (session?.modules ?? []).includes('reporting');
  const currency = session?.tenant.currency ?? 'USD';
  const [failed, setFailed] = useState(false);

  const snapshot = useLive(() => runtime.todayTakings.snapshot(today), null, [today]);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const refresh = useCallback(() => {
    void runtime.todayTakings.refresh(today).then(
      () => {
        if (alive.current) setFailed(false);
      },
      () => {
        if (alive.current) setFailed(true);
      },
    );
  }, [runtime, today]);

  useEffect(() => {
    if (!entitled) return;
    refresh();
    return runtime.subscribeEvents((type) => {
      if (
        type === 'settlement.recorded' ||
        type === 'refund.recorded' ||
        type === 'certificate.issued'
      ) {
        refresh();
      }
    });
  }, [runtime, entitled, refresh]);

  const lastWaiting = useRef(waiting);
  useEffect(() => {
    const before = lastWaiting.current;
    lastWaiting.current = waiting;
    if (entitled && waiting < before) refresh();
  }, [entitled, waiting, refresh]);

  if (!entitled) return null;

  const parts: string[] = [];
  if (snapshot !== null) {
    const {
      by_tender: taken,
      certificate_by_tender: cards,
      by_tender_refunded: given,
    } = snapshot.summary;
    const nets = TENDER_ORDER.map((kind) => ({
      kind,
      cents: (taken[kind] ?? 0) + (cards[kind] ?? 0) - (given[kind] ?? 0),
    })).filter((row) => row.cents !== 0);
    if (nets.length === 0) {
      parts.push(t('today.takingsNone'));
    } else {
      const total = nets.reduce((sum, row) => sum + row.cents, 0);
      parts.push(t('today.takings', { total: formatMoney(total, currency, locale) }));
      for (const row of nets) {
        parts.push(
          t('today.takingsTender', {
            tender: t(`checkout.tender.${row.kind}`),
            amount: formatMoney(row.cents, currency, locale),
          }),
        );
      }
    }
  }
  if (waiting > 0) parts.push(t('today.takingsWaiting', { count: waiting }));
  // Nothing known and nothing queued: no line. A fresh device offline has no
  // honest sentence about today's money, and "nothing taken" would be a guess.
  if (parts.length === 0) return null;
  if (snapshot !== null && (failed || now - Date.parse(snapshot.fetchedAt) > STALE_AFTER_MS)) {
    parts.push(
      t('today.takingsAsOf', { clock: formatClock(snapshot.fetchedAt, timezone, locale) }),
    );
  }

  return <p className="takings">{parts.join(' · ')}</p>;
}

/**
 * Past this, the line says when it was read. Shorter would print a clock after
 * every quiet stretch of a normal day; the events above keep a live iPad well
 * inside it, so crossing it means the stream or the network is not there.
 */
const STALE_AFTER_MS = 2 * 60_000;

/** A sale still in the outbox: a checkout, or a gift card (0070). Counted, never priced. */
function isQueuedSale(entry: { kind: string }): boolean {
  return entry.kind === 'settlement.create' || entry.kind === 'certificate.issue';
}

interface DisplayRow {
  appointment: Appointment | MirroredAppointment;
  pending: boolean;
}

/**
 * Same claim on the same room at the same instant — which is exactly the
 * comparison the exclusion constraint makes, so if this says "same" the server
 * agrees.
 */
function sameBooking(real: Appointment, optimistic: Appointment): boolean {
  return (
    real.slot.resource_id === optimistic.slot.resource_id &&
    real.slot.start === optimistic.slot.start &&
    real.service_id === optimistic.service_id
  );
}

/**
 * Where a booking came from, in one word.
 *
 * `t` is optional and defaults to the device's, so the two other screens that
 * render this badge (`CalendarView`, `Tasks`) pass their live one and nothing
 * else has to.
 */
export function sourceWord(source: string, t: T = deviceT): string {
  switch (source) {
    case 'partner':
      return t('source.partner');
    case 'walk_in':
      return t('source.walkIn');
    case 'guest_web':
      return t('source.guestWeb');
    case 'import':
      return t('source.import');
    default:
      return t('source.counter');
  }
}
